import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clampInset, SafeZoneInsets } from '../../../utils/safeZones';

export interface SafeZoneLayerProps {
  /** The resolved insets for this canvas (% of the canvas). */
  insets: SafeZoneInsets;
  visible: boolean;
  /** SuperAdmin / CS: the four edge handles appear and drags are offered for saving. */
  editable: boolean;
  /** Save-bar wording, e.g. "TikTok" or "Instagram · Reel". */
  label: string;
  /** Publish size, for the px readout next to a handle. */
  publishSize?: { width: number; height: number } | null;
  onSave?: (insets: SafeZoneInsets) => Promise<boolean> | boolean | void;
  /** The host deselects elements while a band is being edited. */
  onEditingChange?: (editing: boolean) => void;
  /**
   * Controlled draft (the Design panel): the host owns the draft and the
   * Save / Cancel UI, so the layer reports every drag through
   * `onDraftChange` and draws no save bar of its own.
   */
  draft?: SafeZoneInsets | null;
  onDraftChange?: (draft: SafeZoneInsets | null) => void;
  className?: string;
}

type Side = keyof SafeZoneInsets;
const SIDES: Side[] = ['top', 'bottom', 'left', 'right'];

const BAND_FILL = 'rgba(239, 68, 68, 0.16)';
/** The safe rectangle's edge: the band colour, solid — one line, one shape. */
const SAFE_EDGE = '1px solid rgb(239, 68, 68)';

/**
 * The keep-out bands of a channel's canvas (everything outside the safe
 * zone), drawn as one semi-transparent shape: four borderless bands plus a
 * single solid outline on the safe rectangle itself (no edge ever runs
 * across a neighbouring band).
 * Editors see the bands; SA / CS can drag the four edges in situ on a real
 * piece of content or template. A drag is never committed silently: a
 * Save / Cancel bar appears, Save writes the universal value for all brands.
 *
 * Positioned absolutely over the canvas (the host's overlay container is the
 * containing block); pointer events only on the handles and the save bar.
 * The root creates no stacking context on purpose: the bands sit under the
 * card's control strips (z-30) so nothing is tinted or blocked, while the
 * handles and the save bar sit above them so they can be grabbed anywhere
 * on the canvas (a strip covered the bottom handle on the card otherwise).
 */
const Z_BANDS = 25;
const Z_HANDLES = 40;
const Z_TAGS = 41;
const Z_SAVEBAR = 42;
const SafeZoneLayer: React.FC<SafeZoneLayerProps> = ({
  insets,
  visible,
  editable,
  label,
  publishSize,
  onSave,
  onEditingChange,
  draft: draftProp,
  onDraftChange,
  className,
}) => {
  const controlled = typeof onDraftChange === 'function';
  const [draftState, setDraftState] = useState<SafeZoneInsets | null>(null);
  const draft = controlled ? (draftProp ?? null) : draftState;
  const draftRef = useRef<SafeZoneInsets | null>(draft);
  draftRef.current = draft;
  const setDraft = useCallback(
    (next: SafeZoneInsets | null | ((prev: SafeZoneInsets | null) => SafeZoneInsets | null)) => {
      const value = typeof next === 'function' ? next(draftRef.current) : next;
      draftRef.current = value;
      if (controlled) onDraftChange?.(value);
      else setDraftState(value);
    },
    [controlled, onDraftChange],
  );
  const [saving, setSaving] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ side: Side; pointerId: number; rect: DOMRect; start: SafeZoneInsets } | null>(null);

  const shown = draft ?? insets;
  const dirty = !!draft && SIDES.some((s) => draft[s] !== insets[s]);

  useEffect(() => { onEditingChange?.(!!draft); }, [draft, onEditingChange]);

  // Escape cancels an edit.
  useEffect(() => {
    if (!draft) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setDraft(null); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [draft, setDraft]);

  const startDrag = (side: Side) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!editable || !rootRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = rootRef.current.getBoundingClientRect();
    dragRef.current = { side, pointerId: e.pointerId, rect, start: { ...shown } };
    setDraft({ ...shown });
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      let pct: number;
      if (d.side === 'top') pct = ((ev.clientY - d.rect.top) / d.rect.height) * 100;
      else if (d.side === 'bottom') pct = ((d.rect.bottom - ev.clientY) / d.rect.height) * 100;
      else if (d.side === 'left') pct = ((ev.clientX - d.rect.left) / d.rect.width) * 100;
      else pct = ((d.rect.right - ev.clientX) / d.rect.width) * 100;
      setDraft((prev) => ({ ...(prev ?? d.start), [d.side]: clampInset(pct) }));
    };
    const onUp = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const save = useCallback(async () => {
    if (!draft || !onSave) return;
    setSaving(true);
    try {
      const ok = await onSave(draft);
      if (ok !== false) setDraft(null);
    } finally {
      setSaving(false);
    }
  }, [draft, onSave]);

  const readout = useMemo(() => {
    const px = (side: Side) => {
      if (!publishSize) return '';
      const base = side === 'top' || side === 'bottom' ? publishSize.height : publishSize.width;
      return ` · ${Math.round((shown[side] / 100) * base)} px`;
    };
    return Object.fromEntries(SIDES.map((s) => [s, `${shown[s].toFixed(1).replace(/\.0$/, '')} %${px(s)}`])) as Record<Side, string>;
  }, [shown, publishSize]);

  if (!visible) return null;

  const t = `${shown.top}%`, b = `${shown.bottom}%`, l = `${shown.left}%`, r = `${shown.right}%`;
  const band: React.CSSProperties = { position: 'absolute', background: BAND_FILL, pointerEvents: 'none', zIndex: Z_BANDS };
  const handle: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(255,255,255,0.95)',
    border: '1px solid rgba(0,0,0,0.55)',
    borderRadius: 3,
    boxShadow: '0 0 2px rgba(0,0,0,0.6)',
    pointerEvents: 'auto',
    zIndex: Z_HANDLES,
  };
  const tag: React.CSSProperties = {
    position: 'absolute',
    fontSize: 10,
    lineHeight: '14px',
    padding: '0 4px',
    borderRadius: 3,
    background: 'rgba(0,0,0,0.75)',
    color: 'white',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: Z_TAGS,
  };

  return (
    <div
      ref={rootRef}
      data-safe-zone-layer=""
      data-editing={draft ? 'true' : undefined}
      className={className}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      aria-label={`Safe zone: ${label}`}
    >
      {/* keep-out bands (no borders of their own) */}
      <div style={{ ...band, left: 0, right: 0, top: 0, height: t }} />
      <div style={{ ...band, left: 0, right: 0, bottom: 0, height: b }} />
      <div style={{ ...band, left: 0, top: t, bottom: b, width: l }} />
      <div style={{ ...band, right: 0, top: t, bottom: b, width: r }} />
      {/* the safe rectangle's edge, one continuous line in the band colour */}
      <div data-safe-zone-edge="" style={{ position: 'absolute', left: l, top: t, right: r, bottom: b, border: SAFE_EDGE, pointerEvents: 'none', zIndex: Z_BANDS, boxSizing: 'border-box' }} />

      {editable && (
        <>
          <div data-safe-zone-handle="top" title="Drag the top band" onPointerDown={startDrag('top')}
            style={{ ...handle, top: `calc(${t} - 5px)`, left: '50%', transform: 'translateX(-50%)', width: 28, height: 10, cursor: 'ns-resize' }} />
          <div data-safe-zone-handle="bottom" title="Drag the bottom band" onPointerDown={startDrag('bottom')}
            style={{ ...handle, bottom: `calc(${b} - 5px)`, left: '50%', transform: 'translateX(-50%)', width: 28, height: 10, cursor: 'ns-resize' }} />
          <div data-safe-zone-handle="left" title="Drag the left band" onPointerDown={startDrag('left')}
            style={{ ...handle, left: `calc(${l} - 5px)`, top: '50%', transform: 'translateY(-50%)', width: 10, height: 28, cursor: 'ew-resize' }} />
          <div data-safe-zone-handle="right" title="Drag the right band" onPointerDown={startDrag('right')}
            style={{ ...handle, right: `calc(${r} - 5px)`, top: '50%', transform: 'translateY(-50%)', width: 10, height: 28, cursor: 'ew-resize' }} />
          {draft && (
            <>
              <span style={{ ...tag, top: `calc(${t} + 4px)`, left: '50%', transform: 'translateX(-50%)' }}>{readout.top}</span>
              <span style={{ ...tag, bottom: `calc(${b} + 4px)`, left: '50%', transform: 'translateX(-50%)' }}>{readout.bottom}</span>
              <span style={{ ...tag, left: `calc(${l} + 4px)`, top: '50%', transform: 'translateY(-50%)' }}>{readout.left}</span>
              <span style={{ ...tag, right: `calc(${r} + 4px)`, top: '50%', transform: 'translateY(-50%)' }}>{readout.right}</span>
            </>
          )}
        </>
      )}

      {draft && !controlled && (
        <div
          data-safe-zone-savebar=""
          style={{
            position: 'absolute', left: '50%', bottom: 8, transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px',
            borderRadius: 999, background: 'rgba(0,0,0,0.85)', color: 'white', fontSize: 11,
            whiteSpace: 'nowrap', pointerEvents: 'auto', zIndex: Z_SAVEBAR,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span>{dirty ? `Save ${label} safe zone for all brands` : `${label} safe zone`}</span>
          <button type="button" onClick={() => setDraft(null)} disabled={saving}
            style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.12)', color: 'white' }}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving}
            style={{ padding: '2px 8px', borderRadius: 999, background: dirty ? '#2563eb' : 'rgba(255,255,255,0.12)', color: 'white', opacity: dirty ? 1 : 0.6 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SafeZoneLayer;
